/**
 * Movement Playground (Phase 3E): one connected space for practising and
 * testing movement (`?level=playground`).
 *
 *  Plaza      open hub with pillars to weave between; the spawn.
 *  Yard       emergent-test area west of the plaza: quarter-pipe, sawtooth ramps,
 *             glide and grip strips, and one station per experiment (A bend
 *             columns, B compression wall, C coupling column). Reactive
 *             surfaces act only while their experiment is enabled.
 *  Descent    long 10° speed run (14 m drop) between rails.               → start
 *  Corner     sharp 90° turn west at the foot of the descent.
 *  Corridor   narrow fast passage with a 3.2 m slot.                       → CP0
 *  U-turn     180° curved outer wall to slide around.                      → CP1
 *  Islands    precision jumps eastbound over a lethal floor, a narrow island
 *             and a lethal bar.                                            → CP2
 *  Terrace    five 0.8 m rises up to the finish.
 *  SHORTCUT   a window in the corner's south wall over a lethal pit straight
 *             onto the terrace pad. Speed preserved down the descent is
 *             what clears it (see PLAYGROUND_GEOMETRY.shortcutGap).
 *
 * The safe route needs no speed beyond run speed. Every area has a waypoint
 * (developer teleport) and the route has checkpoints, so any section can be
 * repeated without replaying the rest.
 *
 * Coordinates: metres, +Y up. The route runs −Z, then −X, then back +X.
 */
import type { LevelData, LightDef, SolidDef, SpawnPoint, TriggerDef } from '../world/LevelData';
import type { SurfaceMaterialDef } from '../world/SurfaceMaterials';
import { arcWall, concaveCurve, ramp, tiledBox } from '../world/LevelBuilders';

type V3 = [number, number, number];
const box = (id: string, c: V3, half: V3, material: string): SolidDef => ({ id, shape: { type: 'box', halfExtents: half }, position: c, material });
const cyl = (id: string, x: number, yBase: number, z: number, radius: number, height: number, material: string): SolidDef => ({
  id,
  shape: { type: 'cylinder', radius, halfHeight: height / 2 },
  position: [x, yBase + height / 2, z],
  material,
});
/** A slab whose top is at `top`, spanning [x0, x1] × [z0, z1]. */
const slab = (id: string, x0: number, x1: number, z0: number, z1: number, top: number, material: string, thickness = 0.5): SolidDef[] =>
  tiledBox(id, [(x0 + x1) / 2, top - thickness / 2, (z0 + z1) / 2], [Math.abs(x1 - x0) / 2, thickness / 2, Math.abs(z1 - z0) / 2], material);
/** A wall from `bottom` to `top`, spanning [x0, x1] × [z0, z1]. */
const wall = (id: string, x0: number, x1: number, z0: number, z1: number, bottom: number, top: number, material = 'safe.greenstone'): SolidDef[] =>
  tiledBox(id, [(x0 + x1) / 2, (bottom + top) / 2, (z0 + z1) / 2], [Math.abs(x1 - x0) / 2, (top - bottom) / 2, Math.abs(z1 - z0) / 2], material);

const MATERIALS: SurfaceMaterialDef[] = [
  { id: 'accent.cyan', kind: 'safe', behaviors: [], visual: { color: 0x0f5560, emissive: 0x39e6ff, emissiveIntensity: 0.55, roughness: 0.5, pattern: 'glyph', opacity: 1 } },
  { id: 'accent.violet', kind: 'safe', behaviors: [], visual: { color: 0x3a2366, emissive: 0xa77bff, emissiveIntensity: 0.5, roughness: 0.5, pattern: 'glyph', opacity: 1 } },
];

const LOW = -14;
const PIT = LOW - 2;
const CORRIDOR_N = -150;
const CORRIDOR_S = -160;
const WINDOW_HALF = 5;
/** North edge of the terrace pad: the far side of the shortcut pit. */
const PAD_N = -169;
const LANE_N = -172;
const LANE_S = -182;
const LANE_Z = (LANE_N + LANE_S) / 2;
const ISLANDS_END_X = -16;
const STAIRS_X = 14;
const STEP_LEN = 1.6;
const TERRACE = LOW + 4;
const TERRACE_X = STAIRS_X + 4 * STEP_LEN;

/** Key positions, exported so route tests follow the level when it is retuned. */
export const PLAYGROUND_GEOMETRY = Object.freeze({
  low: LOW,
  corridorZ: (CORRIDOR_N + CORRIDOR_S) / 2,
  laneZ: LANE_Z,
  /** Pit width between the corridor floor and the terrace pad (shortcut). */
  shortcutGap: CORRIDOR_S - PAD_N,
  padNorthZ: PAD_N,
  stairsX: STAIRS_X,
  terraceX: TERRACE_X,
  terraceY: TERRACE,
});

// Plaza and yard: one open floor ------------------------------------------------
const plaza: SolidDef[] = [
  ...slab('plaza.floor', -110, 30, 20, -30, 0, 'safe.stone'),
  ...[-18, -9, 9, 18].flatMap((x) => [12, 0, -12].map((z) => cyl(`plaza.pillar.${x}.${z}`, x, 0, z, 0.6, 3, 'safe.greenstone'))),
  ...wall('plaza.wallE', 30, 31, 20, -30, -1, 4),
  ...wall('plaza.wallS', -111, 31, 20, 21, -1, 4),
  ...wall('plaza.wallW', -111, -110, 20, -30, -1, 4),
  ...wall('plaza.wallNW', -111, -6.5, -30, -31, -1, 7),
  ...wall('plaza.wallNE', 6.5, 31, -30, -31, -1, 4),
  box('plaza.lip', [0, -0.05, -29.95], [6, 0.05, 0.05], 'accent.cyan'),
];

const SAW_RUN = 1 / Math.tan((15 * Math.PI) / 180);
const yard: SolidDef[] = [
  ...concaveCurve('yard.qp', [-45, 6, -22], 6, 0, 80, 16, 10, 0.5, 'safe.marble'),
  ...[0, 1, 2, 3, 4].flatMap((i) => {
    const x = -60 - 2 * SAW_RUN * i;
    return [...ramp(`yard.saw.u${i}`, [x, 0, 0], [x - SAW_RUN, 1, 0], 6, 0.6, 'safe.marble'), ...ramp(`yard.saw.d${i}`, [x - SAW_RUN, 1, 0], [x - 2 * SAW_RUN, 0, 0], 6, 0.6, 'safe.marble')];
  }),
  ...tiledBox('yard.glide', [-80, 0, -12], [12, 0.01, 3], 'reactive.glide'),
  ...tiledBox('yard.grip', [-80, 0, -20], [12, 0.01, 3], 'reactive.grip'),
  cyl('yard.A.p1', -40, 0, 12, 1.2, 5, 'reactive.bend'),
  cyl('yard.A.p2', -48, 0, 16, 1.2, 5, 'reactive.bend'),
  box('yard.B.wall', [-66, 3, 16], [4, 3, 0.5], 'reactive.compression'),
  cyl('yard.C.column', -86, 0, 12, 3, 4, 'reactive.coupling'),
];

// Descent, corner, corridor ------------------------------------------------------
const descent: SolidDef[] = [
  ...ramp('descent', [0, 0, -30], [0, LOW, -110], 12, 1, 'safe.marble'),
  ...wall('descent.railW', -7, -6, -30, CORRIDOR_N, PIT, 3),
  ...wall('descent.railE', 6, 7, -30, CORRIDOR_S - 0.5, PIT, 3),
  ...slab('runout', -6, 6, -110, CORRIDOR_N, LOW, 'safe.stone'),
];

const corridor: SolidDef[] = [
  ...slab('corridor.floor', -60, 6, CORRIDOR_N, CORRIDOR_S, LOW, 'safe.stone'),
  ...wall('corridor.wallN', -63, -6, CORRIDOR_N + 0.5, CORRIDOR_N, PIT, LOW + 4),
  ...wall('corridor.wallSW', -60, -WINDOW_HALF, CORRIDOR_S, CORRIDOR_S - 0.5, PIT, LOW + 4),
  ...wall('corridor.wallSE', WINDOW_HALF, 6, CORRIDOR_S, CORRIDOR_S - 0.5, PIT, LOW + 4),
  box('corridor.window', [0, LOW - 0.05, CORRIDOR_S + 0.05], [WINDOW_HALF, 0.05, 0.05], 'accent.violet'),
  // The slot: 3.2 m wide for 20 m.
  ...wall('corridor.slotN', -40, -20, CORRIDOR_N, -153.4, LOW, LOW + 4, 'safe.marble'),
  ...wall('corridor.slotS', -40, -20, -156.6, CORRIDOR_S, LOW, LOW + 4, 'safe.marble'),
];

// U-turn ---------------------------------------------------------------------------
const UTURN_C: V3 = [-62, LOW, (CORRIDOR_N + CORRIDOR_S + LANE_N + LANE_S) / 4];
const uturn: SolidDef[] = [
  ...slab('uturn.floor', -60, -80, -148, -184, LOW, 'safe.stone'),
  ...arcWall('uturn.outer', UTURN_C, 17, 90, 270, 24, 4, 0.5, 'accent.violet'),
  // Closes the pit between corridor and lane on the turn's inside.
  ...wall('uturn.pitWall', -60, -59.5, CORRIDOR_S - 0.5, LANE_N + 0.5, PIT, LOW + 2),
];

// Islands: eastbound over a lethal floor --------------------------------------------
const islandZ = (narrow: boolean): [number, number] => (narrow ? [LANE_Z + 0.8, LANE_Z - 0.8] : [LANE_Z + 3, LANE_Z - 3]);
const ISLANDS: [string, number, number, boolean][] = [
  ['i1', -56.4, -54.0, false],
  ['i2', -50.2, -47.8, false],
  ['i3', -43.8, -41.2, true],
  ['i4', -37.6, -35.2, false],
  ['long', -31.6, -19.6, false],
];
const islands: SolidDef[] = [
  ...slab('islands.lethal', -60, ISLANDS_END_X, LANE_N, LANE_S, PIT, 'lethal.basic'),
  ...ISLANDS.flatMap(([id, x0, x1, narrow]) => slab(`islands.${id}`, x0, x1, ...islandZ(narrow), LOW, 'safe.stone')),
  box('islands.bar', [-27, LOW + 0.25, LANE_Z], [0.15, 0.25, 3], 'lethal.basic'),
  ...wall('islands.wallN', -59.5, ISLANDS_END_X, LANE_N, LANE_N + 0.5, PIT, LOW + 3),
  ...wall('islands.wallS', -63, ISLANDS_END_X, LANE_S - 0.5, LANE_S, PIT, LOW + 3),
];

// Shortcut pit, pad, stairs, terrace --------------------------------------------------
const STEP_TOPS = [1, 2, 3, 4].map((k) => LOW + k * 0.8);
const terrace: SolidDef[] = [
  ...slab('pit.lethal', -59.5, TERRACE_X, CORRIDOR_S - 0.5, LANE_N, PIT, 'lethal.basic'),
  ...slab('pad', ISLANDS_END_X, STAIRS_X, PAD_N, -192, LOW, 'safe.stone'),
  box('pad.lip', [0, LOW - 0.05, PAD_N - 0.05], [WINDOW_HALF, 0.05, 0.05], 'accent.violet'),
  ...STEP_TOPS.map((top, i) => box(`stairs.${i}`, [STAIRS_X + (i + 0.5) * STEP_LEN, (top + LOW) / 2, LANE_Z], [STEP_LEN / 2, (top - LOW) / 2, 3], i % 2 ? 'safe.marble' : 'safe.stone')),
  ...tiledBox('terrace', [(TERRACE_X + 40) / 2, (TERRACE + LOW) / 2, (PAD_N - 192) / 2], [(40 - TERRACE_X) / 2, (TERRACE - LOW) / 2, (192 + PAD_N) / 2], 'safe.marble'),
  box('terrace.edge', [TERRACE_X + 0.05, TERRACE - 0.05, LANE_Z], [0.05, 0.05, 3], 'accent.cyan'),
  ...wall('pad.wallW', ISLANDS_END_X - 0.5, ISLANDS_END_X, LANE_S, -192.5, PIT, LOW + 3),
  ...wall('pad.wallS', ISLANDS_END_X - 0.5, 40.5, -192, -192.5, PIT, TERRACE + 3),
  ...wall('terrace.wallE', 40, 40.5, PAD_N, -192, TERRACE, TERRACE + 3),
  ...wall('terrace.wallN', TERRACE_X, 40.5, PAD_N + 0.5, PAD_N, TERRACE, TERRACE + 3),
  // Cheeks beside the stairs, flush with the terrace: the pad's east edge is a wall, not a drop.
  ...wall('stairs.cheekN', STAIRS_X, TERRACE_X, PAD_N, LANE_Z + 3, PIT, TERRACE, 'safe.marble'),
  ...wall('stairs.cheekS', STAIRS_X, TERRACE_X, LANE_Z - 3, -192, PIT, TERRACE, 'safe.marble'),
];

const spawn = (x: number, y: number, z: number, yawDeg: number): SpawnPoint => ({ position: [x, y + 0.02, z], yawDeg });
const SPAWNS = {
  plaza: spawn(0, 0, 10, 0),
  descent: spawn(0, 0, -27, 0),
  runout: spawn(0, LOW, -130, 0),
  corridor: spawn(-11, LOW, -155, 90),
  uturn: spawn(-64, LOW, LANE_Z, -90),
  islands: spawn(-61, LOW, LANE_Z, -90),
  pad: spawn(-8, LOW, LANE_Z, -90),
  yardQp: spawn(-45, 0, -5, 0),
  yardSaw: spawn(-52, 0, 0, 90),
  yardExperiments: spawn(-55, 0, 8, 90),
  yardMaterials: spawn(-62, 0, -16, 90),
};

const triggers: TriggerDef[] = [
  { id: 'start', kind: 'start', center: [0, 1, -28], halfExtents: [6, 2, 0.5], material: 'trigger.start' },
  { id: 'cp.corridor', kind: 'checkpoint', center: [-9, LOW + 2, -155], halfExtents: [0.5, 2, 5], material: 'trigger.checkpoint', checkpointIndex: 0, spawn: SPAWNS.corridor },
  { id: 'cp.uturn', kind: 'checkpoint', center: [-63, LOW + 2, LANE_Z], halfExtents: [0.5, 2, 5], material: 'trigger.checkpoint', checkpointIndex: 1, spawn: SPAWNS.uturn },
  { id: 'cp.pad', kind: 'checkpoint', center: [-10, LOW + 2, LANE_Z], halfExtents: [0.5, 2, 5], material: 'trigger.checkpoint', checkpointIndex: 2, spawn: SPAWNS.pad },
  { id: 'finish', kind: 'finish', center: [34, TERRACE + 4, (PAD_N - 192) / 2], halfExtents: [0.5, 4, (192 + PAD_N) / 2], material: 'trigger.finish' },
];

const lantern = (x: number, y: number, z: number): LightDef => ({ position: [x, y, z], color: 0xffa64d, intensity: 22, distance: 26 });

export const MOVEMENT_PLAYGROUND: LevelData = {
  id: 'movement-playground',
  name: 'Movement Playground',
  version: 1,
  materials: MATERIALS,
  solids: [...plaza, ...yard, ...descent, ...corridor, ...uturn, ...islands, ...terrace],
  triggers,
  start: SPAWNS.plaza,
  killPlaneY: -40,
  lights: [
    lantern(0, 4, 10),
    lantern(-20, 4, -15),
    lantern(-50, 4, 5),
    lantern(-85, 4, -5),
    lantern(0, 1, -60),
    lantern(0, -6, -90),
    lantern(0, -10, -140),
    lantern(-30, -10, -155),
    lantern(-70, -10, -166),
    lantern(-40, -10, -177),
    lantern(0, -10, -180),
    lantern(28, TERRACE + 3, -180),
  ],
  labels: [
    { text: 'DESCENT', position: [0, 4, -29] },
    { text: 'YARD: EXPERIMENTS A B C', position: [-55, 5, 14] },
    { text: 'QUARTER-PIPE', position: [-45, 7, -25] },
    { text: 'SHORTCUT', position: [0, LOW + 5, CORRIDOR_S] },
    { text: 'ISLANDS', position: [-58, LOW + 4, LANE_Z] },
  ],
  waypoints: [
    { name: 'Plaza', spawn: SPAWNS.plaza },
    { name: 'Descent', spawn: SPAWNS.descent },
    { name: 'Runout', spawn: SPAWNS.runout },
    { name: 'Corridor', spawn: SPAWNS.corridor },
    { name: 'U-turn', spawn: SPAWNS.uturn },
    { name: 'Islands', spawn: SPAWNS.islands },
    { name: 'Pad', spawn: SPAWNS.pad },
    { name: 'Yard: quarter-pipe', spawn: SPAWNS.yardQp },
    { name: 'Yard: sawtooth', spawn: SPAWNS.yardSaw },
    { name: 'Yard: experiments', spawn: SPAWNS.yardExperiments },
    { name: 'Yard: glide / grip', spawn: SPAWNS.yardMaterials },
  ],
};
